import {
    ClientsWorkOverviewView,
    ClientsWorkOverviewViewSchema,
} from "@common/types/views/clientsWorkOverviewView";
import { useHasIdentifiedFeatureFlags } from "@components/analytics/featureFlagsIdentity";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useActiveOrganizationRole } from "@hooks/permissions/organization/useActiveOrganizationRole";
import { useUserStatus } from "@hooks/useUserStatus";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "@hooks/useOrganizationUserCache";
import { resolveWorkLanding, type WorkLanding } from "./workLanding";
import { useIsReadyOrTimedOut } from "./useIsReadyOrTimedOut";

/** How long LaunchDarkly gets to identify the user and organization before the page lands on whatever flags it has. */
const FEATURE_FLAGS_WAIT_MS = 3_000;

/** Where the Work page opens. Read it once: the page caches its view right after. */
export function useWorkLanding(): WorkLanding | null {
    const activeOrganization = useActiveOrganization();
    const role = useActiveOrganizationRole();
    const { data: userStatus } = useUserStatus();
    const hasUserAndOrganization =
        activeOrganization !== null && userStatus?.user.id !== undefined;
    const hasFeatureFlags = useIsReadyOrTimedOut({
        isReady: useHasIdentifiedFeatureFlags(),
        isEnabled: hasUserAndOrganization,
        timeoutMs: FEATURE_FLAGS_WAIT_MS,
    });
    const [cachedView] = useOrganizationUserCache<ClientsWorkOverviewView>(
        ORGANIZATION_USER_CACHE_KEYS.CLIENTS_WORK_PAGE_VIEW_CACHE_KEY,
        ClientsWorkOverviewViewSchema,
    );
    const [cachedBaseViewId] = useOrganizationUserCache<string | null>(
        ORGANIZATION_USER_CACHE_KEYS.CLIENTS_WORK_PAGE_BASE_VIEW_ID_CACHE_KEY,
    );

    return resolveWorkLanding({
        isLoading: !hasUserAndOrganization || !hasFeatureFlags,
        role,
        hasCachedView: cachedView !== undefined,
        hasCachedBaseViewId: cachedBaseViewId !== undefined,
    });
}
